import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Paper, Typography, Card, CardContent } from '@mui/material';
import { useQuery, useQueryHistory, useDocumentSummary } from '../../../hooks/useDocs';
import { DEFAULT_TOP_K, MIN_TOP_K, MAX_TOP_K } from '../constants';
import { APP_PAGES } from '../../../pages';
import { getQueryResultCache, saveQueryResultCache } from '../../../Utils/queryResultCache';
import QueryInput from './QueryInput';
import ChunkSlider from './ChunkSlider';
import AnswerCard from './AnswerCard';
import RankedResultsList from './RankedResultsList';
import SourcesList from './SourcesList';
import QueryUsageFooter from './QueryUsageFooter';
import QueryHistory from './QueryHistory';
import TagSections from '../../../Components/TagSections';
import { useCustomerTags } from '../../../hooks/useCustomerTags';

const Query: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';
  const initialTopK = Number(searchParams.get('topK')) || DEFAULT_TOP_K;
  const hasRestoredRef = useRef(false);

  const [query, setQuery] = useState(initialQuery);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [topK, setTopK] = useState(
    initialTopK >= MIN_TOP_K && initialTopK <= MAX_TOP_K ? initialTopK : DEFAULT_TOP_K
  );

  const {
    submitQuery,
    result: queryResult,
    isLoading: isQueryLoading,
    error: queryError,
    restoreResult,
  } = useQuery();
  const { addToHistory } = useQueryHistory();
  const { summary: stats, isLoading: statsLoading } = useDocumentSummary();
  const {
    techTags,
    businessTags,
    techTagsLoading,
    businessTagsLoading,
    techTagsError,
    businessTagsError,
  } = useCustomerTags();

  const syncSearchParams = useCallback(
    (q: string, k: number, replace = false) => {
      setSearchParams({ q, topK: String(k) }, { replace });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
    const parsedTopK = Number(searchParams.get('topK'));
    if (parsedTopK >= MIN_TOP_K && parsedTopK <= MAX_TOP_K) {
      setTopK(parsedTopK);
    }
  }, [initialQuery, searchParams]);

  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const cached = getQueryResultCache();
    const urlQuery = searchParams.get('q')?.trim() || '';
    const urlTopK = Number(searchParams.get('topK')) || DEFAULT_TOP_K;
    const effectiveTopK =
      urlTopK >= MIN_TOP_K && urlTopK <= MAX_TOP_K ? urlTopK : DEFAULT_TOP_K;

    if (cached) {
      const cacheMatchesUrl =
        !urlQuery ||
        (cached.query === urlQuery &&
          cached.topK === effectiveTopK);

      if (cacheMatchesUrl) {
        setQuery(cached.query);
        setTopK(cached.topK);
        restoreResult(cached.result);
        if (!urlQuery) {
          syncSearchParams(cached.query, cached.topK, true);
        }
        return;
      }
    }

    if (urlQuery) {
      submitQuery({ query: urlQuery, topK: effectiveTopK })
        .then((response) => {
          saveQueryResultCache({
            query: urlQuery,
            topK: effectiveTopK,
            result: response,
          });
          addToHistory({
            query: urlQuery,
            answerPreview: response.answer.substring(0, 100),
          });
        })
        .catch(() => {
          // Error handled by hook
        });
    }
    // Run once on mount to restore cached results or re-run URL-driven queries
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runQuery = async (queryText: string) => {
    const trimmedQuery = queryText.trim();
    if (!trimmedQuery) return;

    setQuery(trimmedQuery);

    try {
      const response = await submitQuery({ query: trimmedQuery, topK });
      saveQueryResultCache({ query: trimmedQuery, topK, result: response });
      syncSearchParams(trimmedQuery, topK, true);
      addToHistory({
        query: trimmedQuery,
        answerPreview: response.answer.substring(0, 100),
      });
    } catch {
      // Error handled by hook
    }
  };

  const handleSubmit = async () => {
    await runQuery(query);
  };

  const handleTagClick = (tagName: string) => {
    setSelectedTags((prev) => {
      const isSelected = prev.some((t) => t.toLowerCase() === tagName.toLowerCase());
      const next = isSelected
        ? prev.filter((t) => t.toLowerCase() !== tagName.toLowerCase())
        : [...prev, tagName];
      setQuery(next.join(', '));
      return next;
    });
  };

  const isLoading = isQueryLoading;
  const error = queryError;

  const StatsCard: React.FC<{
    title: string;
    value: number;
    onClick?: () => void;
    testId?: string;
  }> = ({ title, value, onClick, testId }) => (
    <Card
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `View document library, ${statsLoading ? 'loading' : value} ${title}` : undefined}
      data-testid={testId}
      sx={{
        height: '100%',
        border: '1px solid #D9D9D9',
        borderRadius: '0.188rem',
        boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.15)',
        ...(onClick && {
          cursor: 'pointer',
          transition: 'box-shadow 0.2s, border-color 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '-1px 2px 8px rgba(11, 147, 223, 0.25)',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }),
      }}
    >
      <CardContent>
        <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
          {statsLoading ? '—' : value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#F8F8F8', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <StatsCard
              title="Total Documents"
              value={stats.total}
              onClick={() => navigate(APP_PAGES.DOCS_LIBRARY)}
              testId="docs-total-documents-card"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatsCard title="Ready" value={stats.ready} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatsCard title="Processing" value={stats.processing} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatsCard title="Failed" value={stats.failed} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <QueryInput
                value={query}
                onChange={setQuery}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                data-testid="docs-query-input"
              />

              <Box sx={{ mt: 3 }}>
                <ChunkSlider
                  value={topK}
                  onChange={setTopK}
                  min={MIN_TOP_K}
                  max={MAX_TOP_K}
                  data-testid="docs-chunk-slider"
                />
              </Box>
            </Paper>

            {/* Results */}
            {error && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: '#FFEBEE' }}>
                <Typography color="error">{error}</Typography>
              </Paper>
            )}

            {queryResult && (
              <>
                <AnswerCard answer={queryResult.answer} data-testid="docs-answer-card" />
                {queryResult.usage && (
                  <QueryUsageFooter usage={queryResult.usage} data-testid="docs-query-usage" />
                )}
                {queryResult.rankedResults.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <RankedResultsList
                      results={queryResult.rankedResults}
                      data-testid="docs-ranked-results"
                    />
                  </Box>
                )}
                {queryResult.references.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <SourcesList
                      references={queryResult.references}
                      data-testid="docs-references-list"
                    />
                  </Box>
                )}
              </>
            )}

            {!isLoading && !error && !queryResult && query && (
              <Paper sx={{ p: 3 }}>
                <Typography color="text.secondary" textAlign="center">
                  Enter a query above to search your documents
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* Sidebar - Tags & query history */}
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: 'sticky',
                top: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <TagSections
                businessTags={businessTags}
                techTags={techTags}
                isBusinessTagsLoading={businessTagsLoading}
                isTechTagsLoading={techTagsLoading}
                businessTagsError={businessTagsError}
                techTagsError={techTagsError}
                selectedTags={selectedTags}
                onTagClick={(tagName) => handleTagClick(tagName)}
                layout="sidebar"
              />
              <QueryHistory
                onSelectQuery={(q) => {
                  setQuery(q);
                  setSelectedTags([]);
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Query;
